import { Component, inject, signal } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { AuthService, User } from '@auth0/auth0-angular';
import { AngularPlugin, IErrorService } from '@microsoft/applicationinsights-angularplugin-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { VersionCheckService } from '../app/core/services/version-check.service';

import { environment } from '../environments/environment';
import { SpinnerComponent } from './core/spinner/spinner.cmp';
import { MainNavComponent } from './core/main-nav/main-nav.cmp';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'my-app',
    templateUrl: `./app.cmp.html`,
    imports: [
        MainNavComponent,
        RouterOutlet,
        SpinnerComponent,
    ]
})

export class AppComponent {
    private authService = inject(AuthService);
    private _router = inject(Router);
    private versionCheckService = inject(VersionCheckService);

    private _destroy$ = new Subject<void>();

    public authenticated = signal<boolean>(false);
    public isLoading = signal<boolean>(false);

    constructor() {
        this.versionCheckService.initVersionCheck('./version.json');
    }

    ngOnInit(): void {
        this.authService.isLoading$
            .pipe(takeUntil(this._destroy$))
            .subscribe(isLoading => this.isLoading.set(isLoading));

        this.authService.isAuthenticated$
            .pipe(takeUntil(this._destroy$))
            .subscribe(async (isAuthenticated) => {
                this.authenticated.set(isAuthenticated);

                if (isAuthenticated) {
                    this.authService.user$
                        .pipe(takeUntil(this._destroy$))
                        .subscribe(user => {
                            if (user) {
                                try {
                                    this.addAppInsights(user);
                                }
                                catch (error) {
                                    console.error('[AppComponent] Error initializing Cobrowse/AppInsights:', error);
                                }
                            }
                        });
                }
                else {
                    // Handle unauthenticated state
                    if (!this.isLoading()) {
                        try {
                            this.authService.loginWithRedirect({
                                appState: {
                                    target: this._router.url
                                }
                            });
                        }
                        catch (error) {
                            console.error('[AppComponent] Error initiating login redirect:', error);
                        }
                    }
                }
            });

        this.authService.error$
            .pipe(takeUntil(this._destroy$))
            .subscribe(error => {
                if (error) {
                    console.error('[AppComponent] Auth0 Error detected:', error);
                    console.error('[AppComponent] Error type:', (error as any)?.error);
                    console.error('[AppComponent] Error description:', (error as any)?.error_description);

                    // Force redirect to login on authentication errors
                    try {
                        this.authService.loginWithRedirect({
                            appState: {
                                target: this._router.url
                            }
                        });
                    }
                    catch (redirectError) {
                        console.error('[AppComponent] Error during forced login redirect:', redirectError);
                    }
                }
            });
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    private addAppInsights(user: User) {
        var angularPlugin = new AngularPlugin();
        const appInsights = new ApplicationInsights({
            config: {
                instrumentationKey: environment.appInsightsKey,
                extensions: [angularPlugin],
                extensionConfig: {
                    [angularPlugin.identifier]: {
                        router: this._router,
                        errorServices: [new CustomErrorHandler()]
                    }
                }
            }
        });

        appInsights.loadAppInsights();
        if (user?.email) {
            appInsights.setAuthenticatedUserContext(user.email, user.email, true);
        }
    }
}

export class CustomErrorHandler implements IErrorService {
    constructor() {
    }

    handleError(error: any) {
        if (error && !environment.production) {
            console.error(error);
        }
    }
}